import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpApi from 'i18next-http-backend';

import * as countries from 'i18n-iso-countries';
import enCountryData from 'i18n-iso-countries/langs/en.json';
import nlCountryData from 'i18n-iso-countries/langs/nl.json';
import type { GridOptions } from 'ag-grid-community';
import api from '@/helpers/api';
import { supportedLanguages } from '@cutr/constants/i18n';
import {
  createI18nUsageReporter,
  I18N_USAGE_INIT_OPTIONS,
} from '@cutr/constants/i18n-usage';

const NAMESPACES = [
  'common',
  'cutlist-admin',
  'home',
  'files',
  'order-files',
  'order',
  'order-todo',
  'order-delivery',
  'order-checklist',
  'order-materials',
  'order-project',
  'billing',
  'order-quote',
  'costdoc',
  'rfq',
  'customer',
  'maker',
  'navigation',
  'auth',
  'settings',
  'address',
  'business',
  'workspace',
  'formulate',
  'address-facilities',
  'materials',
  'invoices',
  'ag-grid',
  'landing-page',
  'dxf-conversion',
  'accept-pricing',
  'offer-response',
  'pricing-change-response',
  'cutlist',
  'messages',
  'notifications',
  'order-checkout',
  'order-contacts',
  'cabinator',
  'hardware',
  // pricing-document is reserved for server-side translations
  'pricing-change',
  'order-timeline',
  'issues',
  'positions',
];

const isDev = import.meta.env.DEV && !import.meta.env.VITEST;
// Register each supported language:
countries.registerLocale(enCountryData);
countries.registerLocale(nlCountryData);

const getSupportedLanguages = () => {
  if (
    ['development', 'staging', 'preview'].includes(
      import.meta.env.VITE_CUTR_ENV
    )
  ) {
    // return FR when development in staging to allow testing French without rolling it out to production (CTR-9235)
    return ['en', 'nl', 'fr', 'dev'];
  }
  return supportedLanguages;
};

export const i18nUsage = createI18nUsageReporter('ui');

i18next
  .use(HttpApi)
  .use(LanguageDetector)
  .use(i18nUsage.postProcessor)
  .init(
    // When making changes, make sure to update types/i18next.d.ts as well!
    {
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
      },
      // Most customers are Dutch, so default to `nl` instead of detecting the browser
      // language (CTR-15777). Manual selection via LangSelector still works through
      // the querystring/cookie/localStorage caches.
      detection: {
        order: ['querystring', 'cookie', 'localStorage', 'sessionStorage'],
        caches: ['cookie', 'localStorage'],
      },
      returnEmptyString: false,
      load: 'languageOnly',
      ns: NAMESPACES,
      debug: false,
      fallbackLng: ['nl', 'en', 'dev'],
      supportedLngs: getSupportedLanguages(),
      appendNamespaceToCIMode: true,
      returnNull: false,
      ...I18N_USAGE_INIT_OPTIONS,
    }
  )
  .then((value) => {
    // i18next does not set resolvedLanguage if language is `dev` because `en` translations are not available. This should only happen in development mode.
    if (isDev && !i18next.resolvedLanguage) {
      i18next.resolvedLanguage = 'en';
    }
    importServerTranslations();
    return value;
  });

export default i18next;

export const AgGridGetLocaleText: GridOptions['getLocaleText'] = (params) =>
  i18next.t(`ag-grid:${params.key}`);

async function importServerTranslations() {
  return api.get('/v2/pricing-document/locales/all').then(({ data }) => {
    Object.keys(data).forEach((lng) => {
      i18next.addResourceBundle(lng, 'pricing-document', data[lng]);
    });
  });
}
